import React, { useState, useMemo, useEffect } from 'react';
import { format, addDays, startOfWeek, subWeeks, addWeeks } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Trash2, Copy, Save, Send, Sparkles, History, MoreVertical, AlertCircle, XCircle, RefreshCw, ChevronDown, ChevronUp, Info, RotateCcw, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ProjectSelector } from './ProjectSelector';
import { CostCentreCombobox } from './CostCentreCombobox';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useTour } from '@/contexts/TourContext';
import type { TimesheetEntry } from '@/types/timesheet';
import { approvalsApi } from '@/services/approvalsApi';
import { timesheetApi } from '@/services/timesheetApi';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { aiService } from '@/services/aiService';
import { rejectionService } from '@/services/rejectionService';
import { AnomalyAlertsPanelEnhanced } from './AIAssistant/AnomalyAlertsPanelEnhanced';
import { SmartAutoFillDialog } from './AIAssistant/SmartAutoFillDialog';
import { QuickEntryModalEnhanced } from './AIAssistant/QuickEntryModalEnhanced';
import { AuditHistoryDialog } from './AuditHistory';
import { BookingHoverCard, BookingActionMenu, ManualEntryActionMenu, BookingDetailsModal, DismissBookingModal } from './ScheduledBookings';
import { autoLoadScheduledBookings, hasEditedHours, dismissBooking, restoreBooking } from '@/services/scheduledBookingService';
import type { StatusDotType } from './ScheduledBookings/StatusDot';
import { AbsenceIndicator, ReadOnlyIndicator } from './AbsenceBadge';
import type { AbsenceType } from './AbsenceBadge';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Navigation state types for drilldown from reports
type TimesheetDrilldownState = {
  employeeId: string;
  weekEnding: Date;
};

interface TimesheetGridProps {
  selection?: TimesheetDrilldownState;
}

export function TimesheetGrid({ selection }: TimesheetGridProps = {}) {
  const { 
    projects, 
    costCentres, 
    currentEmployee, 
    employees,
    timesheets, 
    updateTimesheet,
    toggleCostCentreFavourite,
    updateCostCentreLastUsed
  } = useTimesheetForScreen('timesheet');
  const { getScreenMode } = useDataSource();
  const { startPendingTour } = useTour();
  
  // Get current screen mode for workflow integration
  const screenMode = getScreenMode('timesheet');
  
  // Determine which employee's timesheet to display
  const targetEmployeeId = selection?.employeeId ?? currentEmployee.id;
  
  // Initialize week based on selection or default to current week
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    selection?.weekEnding
      ? startOfWeek(selection.weekEnding, { weekStartsOn: 1 })
      : startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  // Check for pending tours when component mounts
  useEffect(() => {
    startPendingTour();
  }, [startPendingTour]);

  // Get or create timesheet for current week
  const currentTimesheet = useMemo(() => {
    const weekEnding = addDays(currentWeekStart, 6);
    
    const existing = timesheets.find(
      ts => ts.employeeId === targetEmployeeId && 
      format(ts.weekEnding, 'yyyy-MM-dd') === format(weekEnding, 'yyyy-MM-dd')
    );
    
    if (existing) {
      return existing;
    }
    
    // Fallback: only create new timesheets for the actual current user
    return {
      id: `ts-new-${Date.now()}`,
      employeeId: currentEmployee.id,
      employeeName: currentEmployee.name,
      weekEnding,
      status: 'draft' as const,
      entries: []
    };
  }, [currentWeekStart, timesheets, targetEmployeeId, currentEmployee]);

  const [entries, setEntries] = useState<TimesheetEntry[]>(currentTimesheet.entries);
  const [showAutoFillDialog, setShowAutoFillDialog] = useState(false);
  const [showQuickEntryModal, setShowQuickEntryModal] = useState(false);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [dismissedWarnings, setDismissedWarnings] = useState<Set<string>>(new Set());
  
  // Scheduled bookings state
  const [selectedBookingForDetails, setSelectedBookingForDetails] = useState<TimesheetEntry | null>(null);
  const [selectedBookingForDismiss, setSelectedBookingForDismiss] = useState<TimesheetEntry | null>(null);
  const [hasLoadedScheduledBookings, setHasLoadedScheduledBookings] = useState(false);

  // Track editing state for hours inputs to prevent premature autocomplete
  const [editingHours, setEditingHours] = useState<Record<string, string | undefined>>({});
  const getHoursCellKey = (entryId: string, day: typeof DAYS[number]) => `${entryId}-${day}`;

  // Sync entries when timesheet changes(id or entries from context)
  // We need to sync when:
  // 1. The timesheet ID changes (navigating to different week)
  // 2. The entries in the context change (after submission, the backend returns updated timesheet)
  React.useEffect(() => {
    console.log('📋 Syncing entries from timesheet:', currentTimesheet.id, 'Entries count:', currentTimesheet.entries.length);
    setEntries(currentTimesheet.entries);
    setHasLoadedScheduledBookings(false); // Reset flag when timesheet changes
  }, [currentTimesheet.id, currentTimesheet.entries]);

  // Monitor entries changes for debugging
  React.useEffect(() => {
    console.log('📊 Entries state updated. Total entries:', entries.length);
    if (entries.length > 0) {
      console.log('📝 Current entries:', entries.map(e => ({
        id: e.id,
        projectId: e.projectId,
        hasProject: !!e.projectId
      })));
    }
  }, [entries]);

  // AI-powered anomaly detection
  const aiWarnings = useMemo(() => {
    const warnings = aiService.detectAnomalies(
      entries, 
      currentWeekStart, 
      timesheets,
      currentEmployee?.workingPattern
    );
    return warnings.filter(w => !dismissedWarnings.has(w.id));
  }, [entries, currentWeekStart, timesheets, currentEmployee, dismissedWarnings]);

  // AI-powered auto-fill suggestions
  const autoFillSuggestions = useMemo(() => {
    return aiService.generateAutoFillSuggestions(
      targetEmployeeId,
      timesheets,
      currentWeekStart
    );
  }, [targetEmployeeId, timesheets, currentWeekStart]);

  // Calculate totals
  const rowTotals = useMemo(() => {
    return entries.map(entry => 
      Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0)
    );
  }, [entries]);

  const columnTotals = useMemo(() => {
    return DAYS.map(day => 
      entries.reduce((sum, entry) => sum + entry.hours[day], 0)
    );
  }, [entries]);

  const weekTotal = useMemo(() => 
    columnTotals.reduce((sum, total) => sum + total, 0)
  , [columnTotals]);

  // Check for missing descriptions (used for submission validation)
  const hasMissingDescriptions = useMemo(() => {
    return entries.some(entry => !entry.description.trim());
  }, [entries]);

  const addRow = () => {
    const newEntry: TimesheetEntry = {
      id: `entry-${Date.now()}`,
      projectId: '',
      costCentreId: '',
      description: '',
      hours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      }
    };
    console.log('🆕 Adding new row:', newEntry);
    setEntries([...entries, newEntry]);
    console.log('📊 Total entries after add:', entries.length + 1);
    toast.success('New row added');
  };

  const deleteRow = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
    toast.success('Row deleted');
  };

  const duplicateRow = (index: number) => {
    const entry = entries[index];
    const newEntry: TimesheetEntry = {
      ...entry,
      id: `entry-${Date.now()}`,
      hours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      }
    };
    setEntries([...entries.slice(0, index + 1), newEntry, ...entries.slice(index + 1)]);
    toast.success('Row duplicated');
  };

  const updateEntry = (index: number, field: keyof TimesheetEntry, value: any) => {
    console.log('🔄 Updating entry:', { index, field, value, totalEntries: entries.length });
    
    setEntries(prevEntries => {
      if (index < 0 || index >= prevEntries.length) {
        console.error('❌ Invalid index:', index, 'Total entries:', prevEntries.length);
        toast.error('Error updating entry: Invalid row index');
        return prevEntries;
      }
      
      const updated = [...prevEntries];
      updated[index] = { ...updated[index], [field]: value };
      console.log('✅ Updated entry:', updated[index]);
      
      // Show success feedback for project selection
      if (field === 'projectId' && value) {
        const project = projects.find(p => p.id === value);
        if (project) {
          console.log('✅ Project selected:', project.name);
          toast.success(`Selected: ${project.name}`);
        }
      }
      
      return updated;
    });
  };

  const updateHours = (index: number, day: typeof DAYS[number], value: string) => {
    // Parse the value, allowing empty string to become 0
    const hours = value === '' ? 0 : parseFloat(value);
    // Validate and round to nearest 0.10 (6-minute interval)
    const roundedHours = isNaN(hours) ? 0 : Math.round(hours * 10) / 10;
    const updated = [...entries];
    updated[index] = {
      ...updated[index],
      hours: { ...updated[index].hours, [day]: roundedHours }
    };
    setEntries(updated);
  };

  const copyFromLastWeek = () => {
    const lastWeek = subWeeks(currentWeekStart, 1);
    const lastWeekEnding = addDays(lastWeek, 6);
    const lastTimesheet = timesheets.find(
      ts => ts.employeeId === targetEmployeeId && 
      format(ts.weekEnding, 'yyyy-MM-dd') === format(lastWeekEnding, 'yyyy-MM-dd')
    );

    if (lastTimesheet && lastTimesheet.entries.length > 0) {
      setEntries(lastTimesheet.entries.map(entry => ({
        ...entry,
        id: `entry-${Date.now()}-${Math.random()}`,
        hours: {
          monday: 0,
          tuesday: 0,
          wednesday: 0,
          thursday: 0,
          friday: 0,
          saturday: 0,
          sunday: 0
        }
      })));
      toast.success('Copied projects from last week');
    } else {
      toast.error('No timesheet found for last week');
    }
  };

  const saveTimesheet = () => {
    const updated = {
      ...currentTimesheet,
      entries,
      status: 'draft' as const
    };
    updateTimesheet(updated);
    toast.success('Timesheet saved as draft');
  };

  const submitTimesheet = async () => {
    if (entries.length === 0) {
      toast.error('Cannot submit empty timesheet');
      return;
    }

    if (hasMissingDescriptions) {
      toast.error('Please add descriptions to all entries');
      return;
    }

    // Check minimum hours requirement based on employee's working pattern
    const expectedWeeklyHours = currentEmployee?.workingPattern?.hoursPerWeek || 40;
    const totalHours = entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0);
    
    const minimumHoursThreshold = expectedWeeklyHours * 0.8; // Allow 20% tolerance
    
    if (totalHours < minimumHoursThreshold) {
      toast.error(
        `Insufficient hours logged`,
        {
          description: `You have logged ${totalHours.toFixed(1)}h but need at least ${minimumHoursThreshold.toFixed(1)}h (${expectedWeeklyHours}h expected). Please add missing time entries.`,
          duration: 6000,
        }
      );
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast.loading('Submitting timesheet...');

      if (screenMode === 'live') {
        // LIVE MODE: Use backend API to trigger workflow engine
        console.log('🔄 Submitting timesheet in LIVE mode via backend API...');
        
        // Prepare timesheet with entries
        const timesheetToSubmit = {
          ...currentTimesheet,
          entries,
        };
        
        // Check if this is a new timesheet that needs to be created first
        const isNewTimesheet = currentTimesheet.id.startsWith('ts-new-');
        let timesheetId = currentTimesheet.id;
        
        if (isNewTimesheet) {
          console.log('📝 Creating new timesheet before submission...');
          const createdTimesheet = await timesheetApi.createTimesheet({
            ...timesheetToSubmit,
            id: '',
            status: 'draft',
          });
          timesheetId = createdTimesheet.id;
          console.log(`✅ Created timesheet with ID: ${timesheetId}`);
        } else {
          // Update existing timesheet with current entries before submitting
          console.log('📝 Updating timesheet entries before submission...');
          await timesheetApi.updateTimesheet(timesheetId, {
            ...timesheetToSubmit,
            status: 'draft', // Keep as draft, submit endpoint will change status
          });
        }
        
        // Now call the submit endpoint to trigger the workflow engine
        console.log(`🚀 Calling submit API for timesheet ${timesheetId}...`);
        const submitResponse = await approvalsApi.submitTimesheet(timesheetId);
        
        // Dismiss loading toast
        toast.dismiss(loadingToast);
        
        if (submitResponse.message) {
          toast.success(submitResponse.message, {
            description: submitResponse.workflowInstanceId 
              ? 'Workflow started successfully' 
              : undefined,
            duration: 4000,
          });
        } else {
          toast.success('Timesheet submitted for approval');
        }
        
        // Fetch the updated timesheet from the server to get the latest state
        const updatedTimesheet = await timesheetApi.getTimesheetById(timesheetId);
        if (updatedTimesheet) {
          updateTimesheet(updatedTimesheet);
        }
        
        console.log('✅ Timesheet submitted successfully via backend workflow engine');
      } else {
        // DEMO MODE: Use client-side workflow simulation (existing behavior)
        console.log('🔄 Submitting timesheet in DEMO mode via client-side workflow...');
        
        // Import the submission service dynamically
        const { submitTimesheetWithWorkflow } = await import('@/services/timesheetSubmissionService');

        // Prepare timesheet with entries
        const timesheetToSubmit = {
          ...currentTimesheet,
          entries,
        };

        // Submit with full workflow integration (client-side)
        const result = await submitTimesheetWithWorkflow(
          timesheetToSubmit,
          currentEmployee,
          employees,
          timesheets
        );

        // Dismiss loading toast
        toast.dismiss(loadingToast);

        if (result.success) {
          // Update the timesheet in context
          updateTimesheet(result.timesheet);

          // Show success message with details
          if (result.autoApproved) {
            toast.success(result.message, {
              description: result.workflowName ? `Via ${result.workflowName}` : undefined,
              duration: 5000,
            });
          } else {
            toast.success(result.message, {
              description: result.workflowName ? `Via ${result.workflowName}` : undefined,
              duration: 4000,
            });
          }

          // Log applied automation rules if any
          if (result.appliedRules && result.appliedRules.length > 0) {
            console.log('✅ Applied automation rules:', result.appliedRules);
          }
        } else {
          toast.error('Failed to submit timesheet');
        }
      }
    } catch (error) {
      console.error('Error submitting timesheet:', error);
      toast.error('Failed to submit timesheet. Please try again.');
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    setCurrentWeekStart(prev => 
      direction === 'prev' ? subWeeks(prev, 1) : addWeeks(prev, 1)
    );
  };

  // AI Feature Handlers
  const handleDismissWarning = (warningId: string) => {
    setDismissedWarnings(prev => new Set([...prev, warningId]));
  };

  const handleApplyAutoFill = (suggestions: any[]) => {
    const newEntries = suggestions.map(suggestion => ({
      id: `entry-${Date.now()}-${Math.random()}`,
      projectId: suggestion.projectId,
      costCentreId: suggestion.costCentreId,
      description: suggestion.description,
      hours: suggestion.hours,
    }));
    setEntries([...entries, ...newEntries]);
    toast.success(`Added ${suggestions.length} entries from AI suggestions`);
  };

  const handleNaturalLanguageEntry = (entry: Partial<TimesheetEntry>) => {
    const newEntry: TimesheetEntry = {
      id: `entry-${Date.now()}`,
      projectId: entry.projectId || '',
      costCentreId: entry.costCentreId || '',
      description: entry.description || '',
      hours: entry.hours || {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0,
      },
    };
    setEntries([...entries, newEntry]);
  };

  // Scheduled booking handlers
  const handleDismissBooking = (index: number, reason: string, customReason?: string) => {
    const entry = entries[index];
    const dismissedEntry = dismissBooking(entry, customReason || reason);
    
    const updated = [...entries];
    updated[index] = dismissedEntry;
    setEntries(updated);
    
    toast.success('Booking dismissed', {
      description: 'You can still add hours manually or delete this row',
    });
  };

  const handleRestoreBooking = (index: number) => {
    const entry = entries[index];
    const restoredEntry = restoreBooking(entry);
    
    const updated = [...entries];
    updated[index] = restoredEntry;
    setEntries(updated);
    
    toast.success('Booking restored', {
      description: 'Original scheduled hours have been restored',
    });
  };

  // Get rejection history for current timesheet
  const rejectionHistory = useMemo(() => {
    if (!currentTimesheet.id || currentTimesheet.id.startsWith('ts-new-')) {
      return null;
    }
    return rejectionService.getTimesheetRejectionHistory(currentTimesheet.id);
  }, [currentTimesheet.id]);

  // Get the most recent pending rejection
  const pendingRejection = useMemo(() => {
    if (!rejectionHistory || rejectionHistory.rejections.length === 0) {
      return null;
    }
    return rejectionHistory.rejections.find(r => r.status === 'pending_correction') || null;
  }, [rejectionHistory]);

  // State for rejection details expansion
  const [showRejectionDetails, setShowRejectionDetails] = useState(true);

  // Handle resubmit
  const handleResubmit = () => {
    if (entries.length === 0) {
      toast.error('Cannot submit empty timesheet');
      return;
    }

    if (hasMissingDescriptions) {
      toast.error('Please add descriptions to all entries');
      return;
    }

    // Update rejection status to resubmitted
    if (pendingRejection) {
      rejectionService.updateRejectionStatus(pendingRejection.id, 'resubmitted');
    }

    const updated = {
      ...currentTimesheet,
      entries,
      status: 'submitted' as const,
      submittedAt: new Date()
    };
    updateTimesheet(updated);
    toast.success('Timesheet resubmitted for approval');
  };

  const isReadOnly = currentTimesheet.status !== 'draft' && currentTimesheet.status !== 'rejected';
  const isRejected = currentTimesheet.status === 'rejected' && pendingRejection;

  // Auto-load scheduled bookings when timesheet is opened
  React.useEffect(() => {
    if (!isReadOnly && !hasLoadedScheduledBookings && entries.length >= 0) {
      const weekEnding = addDays(currentWeekStart, 6);
      const entriesWithScheduled = autoLoadScheduledBookings(
        targetEmployeeId,
        weekEnding,
        entries
      );
      
      // Only update if we actually added scheduled bookings
      const addedCount = entriesWithScheduled.length - entries.length;
      if (addedCount > 0) {
        setEntries(entriesWithScheduled);
        toast.success(`${addedCount} scheduled booking${addedCount > 1 ? 's' : ''} loaded`, {
          description: 'Review and edit hours as needed',
        });
      }
      
      setHasLoadedScheduledBookings(true);
    }
  }, [isReadOnly, hasLoadedScheduledBookings, entries.length, currentWeekStart, targetEmployeeId]);

  return (
    <div className="space-y-2 sm:space-y-3">
      {/* Mode Indicator Banner */}
      {/* <ModeIndicatorBanner
        mode={mode}
        screenName="My Timesheet"
        onNavigateToSettings={() => {
          // Navigate to settings - you can implement this based on your routing
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      /> */}

      {/* Header - Week Navigation Only */}
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={() => navigateWeek('prev')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-center">
            <h2 className="text-base sm:text-lg font-semibold">
              Week: {format(currentWeekStart, 'dd MMM')} - {format(addDays(currentWeekStart, 6), 'dd MMM yyyy')}
            </h2>
            <Badge variant={
              currentTimesheet.status === 'draft' ? 'secondary' :
              currentTimesheet.status === 'submitted' ? 'default' :
              currentTimesheet.status === 'approved' ? 'default' :
              'secondary'
            } className="text-xs">
              {currentTimesheet.status.toUpperCase()}
            </Badge>
          </div>
          <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={() => navigateWeek('next')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        {/* View History Button */}
        {currentTimesheet.id && !currentTimesheet.id.startsWith('ts-new-') && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAuditHistory(true)}
            className="gap-2"
          >
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">View History</span>
          </Button>
        )}
      </div>

      {/* Rejection Status Banner - Prominent Display */}
      {isRejected && pendingRejection && (
        <Alert className="border-2 border-red-500 bg-red-50 dark:bg-red-950/20">
          <div className="flex items-start gap-3">
            <XCircle className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <AlertTitle className="text-red-900 dark:text-red-100 text-lg font-bold flex items-center justify-between">
                <span>Timesheet Rejected - Action Required</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRejectionDetails(!showRejectionDetails)}
                  className="h-8 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30"
                >
                  {showRejectionDetails ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-1" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      Show Details
                    </>
                  )}
                </Button>
              </AlertTitle>
              
              <AlertDescription className="text-red-800 dark:text-red-200 space-y-3">
                {/* Rejection Summary */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                  <span className="font-medium">Rejected by:</span>
                  <span>{pendingRejection.approverName}</span>
                  <span className="hidden sm:inline text-red-600 dark:text-red-400">•</span>
                  <span className="font-medium">Date:</span>
                  <span>{format(pendingRejection.rejectionDate, 'EEEE, MMMM d, yyyy \'at\' h:mm a')}</span>
                </div>

                {/* Rejection Reason - Always Visible */}
                <div className="bg-red-100 dark:bg-red-900/40 border-2 border-red-300 dark:border-red-700 p-4 rounded-lg">
                  <div className="font-bold text-red-900 dark:text-red-100 mb-2 text-base">
                    📋 Rejection Reason:
                  </div>
                  <div className="text-red-900 dark:text-red-100 whitespace-pre-wrap">
                    {pendingRejection.rejectionReason}
                  </div>
                </div>

                {/* Expandable Details */}
                {showRejectionDetails && (
                  <div className="space-y-3 pt-2 border-t border-red-300 dark:border-red-700">
                    {/* Affected Entries */}
                    {pendingRejection.affectedEntries.length > 0 && (
                      <div>
                        <div className="font-semibold text-red-900 dark:text-red-100 mb-2">
                          Affected Entries ({pendingRejection.affectedEntries.length}):
                        </div>
                        <div className="space-y-2">
                          {pendingRejection.affectedEntries.map((entry, index) => (
                            <div 
                              key={index}
                              className="bg-white dark:bg-gray-900 border border-red-300 dark:border-red-700 p-3 rounded text-sm"
                            >
                              <div className="font-medium text-red-900 dark:text-red-100 mb-1">
                                {entry.projectName}
                              </div>
                              <div className="text-muted-foreground text-xs mb-1">
                                Cost Centre: {entry.costCentreName}
                              </div>
                              {entry.description && (
                                <div className="text-muted-foreground text-xs mb-2">
                                  Description: {entry.description}
                                </div>
                              )}
                              <div className="flex gap-2 flex-wrap">
                                {Object.entries(entry.hours).map(([day, hours]) => 
                                  hours > 0 ? (
                                    <span key={day} className="text-xs bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-100 px-2 py-1 rounded">
                                      {day.substring(0, 3)}: {hours}h
                                    </span>
                                  ) : null
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Instructions */}
                    <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-300 dark:border-orange-700 p-3 rounded">
                      <div className="font-semibold text-orange-900 dark:text-orange-100 mb-1 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        What to do next:
                      </div>
                      <ol className="list-decimal list-inside text-sm text-orange-800 dark:text-orange-200 space-y-1">
                        <li>Review the rejection reason above</li>
                        <li>Make necessary corrections to your timesheet entries</li>
                        <li>Click "Resubmit for Approval" when ready</li>
                      </ol>
                    </div>
                  </div>
                )}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {/* Zone 1: Data Entry Toolbar - STICKY - Redesigned Single Row Layout */}
      {!isReadOnly && (
        <div className="sticky top-0 z-20 bg-background pb-2">
          <Card className="border-primary/20 bg-primary/5 shadow-md">
            <CardContent className="p-3 sm:p-4">
              {/* Desktop Layout (≥1024px) - Single Row with Visual Grouping */}
              <div className="hidden lg:flex items-center gap-3">
                {/* Primary Action */}
                <Button 
                  onClick={addRow} 
                  className="gap-2 min-w-[120px]"
                  title="Add a new timesheet row (Alt+A)"
                  accessKey="a"
                  data-tour="add-row"
                >
                  <Plus className="h-4 w-4" />
                  Add Row
                </Button>

                {/* Divider */}
                <div className="h-8 w-px bg-border" aria-hidden="true" />

                {/* Input Action - Flexible Width */}
                <Button
                  variant="outline"
                  onClick={() => setShowQuickEntryModal(true)}
                  className="gap-2 flex-1 max-w-[320px]"
                  title="Quick entry with natural language (Alt+Q)"
                  accessKey="q"
                >
                  <Sparkles className="h-4 w-4" />
                  Quick Entry
                </Button>

                {/* Divider */}
                <div className="h-8 w-px bg-border" aria-hidden="true" />

                {/* Utility Actions */}
                <Button 
                  variant="outline" 
                  onClick={copyFromLastWeek} 
                  className="gap-2 min-w-[160px]"
                  title="Copy timesheet from last week (Alt+C)"
                  accessKey="c"
                >
                  <Copy className="h-4 w-4" />
                  Copy Last Week
                </Button>
                
                {autoFillSuggestions.length > 0 ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAutoFillDialog(true)} 
                    className="gap-2 min-w-[160px] relative"
                    title="AI-powered autofill suggestions (Alt+F)"
                    accessKey="f"
                  >
                    <Sparkles className="h-4 w-4" />
                    Smart Autofill
                    <span className="absolute -top-1 -right-1 flex h-3 w-3" aria-hidden="true">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-teal-500"></span>
                    </span>
                    <Badge variant="secondary" className="text-xs ml-1">
                      {autoFillSuggestions.length}
                    </Badge>
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAutoFillDialog(true)} 
                    className="gap-2 min-w-[160px]"
                    title="AI-powered autofill suggestions (Alt+F)"
                    accessKey="f"
                    disabled
                  >
                    <Sparkles className="h-4 w-4" />
                    Smart Autofill
                  </Button>
                )}
              </div>

              {/* Tablet Layout (768px - 1023px) - Compact Single Row */}
              <div className="hidden md:flex lg:hidden items-center gap-2">
                <Button 
                  onClick={addRow} 
                  className="gap-2 min-w-[110px]"
                  title="Add a new timesheet row (Alt+A)"
                  accessKey="a"
                >
                  <Plus className="h-4 w-4" />
                  Add Row
                </Button>
                <div className="h-8 w-px bg-border" aria-hidden="true" />
                <Button
                  variant="outline"
                  onClick={() => setShowQuickEntryModal(true)}
                  className="gap-2 flex-1"
                  title="Quick entry with natural language (Alt+Q)"
                  accessKey="q"
                >
                  <Sparkles className="h-4 w-4" />
                  Quick Entry
                </Button>
                <div className="h-8 w-px bg-border" aria-hidden="true" />
                <Button 
                  variant="outline" 
                  onClick={copyFromLastWeek} 
                  className="gap-2 min-w-[100px]"
                  title="Copy timesheet from last week (Alt+C)"
                  accessKey="c"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
                {autoFillSuggestions.length > 0 ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAutoFillDialog(true)} 
                    className="gap-2 min-w-[110px] relative"
                    title="AI-powered autofill suggestions (Alt+F)"
                    accessKey="f"
                  >
                    <Sparkles className="h-4 w-4" />
                    Autofill
                    <span className="absolute -top-1 -right-1 flex h-3 w-3" aria-hidden="true">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-teal-500"></span>
                    </span>
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAutoFillDialog(true)} 
                    className="gap-2 min-w-[110px]"
                    title="AI-powered autofill suggestions (Alt+F)"
                    accessKey="f"
                    disabled
                  >
                    <Sparkles className="h-4 w-4" />
                    Autofill
                  </Button>
                )}
              </div>

              {/* Mobile Layout (<768px) - Icon + Input + Overflow Menu */}
              <div className="flex md:hidden items-center gap-2">
                <Button 
                  onClick={addRow} 
                  size="lg"
                  className="gap-2 min-h-[44px]"
                  title="Add a new timesheet row (Alt+A)"
                  accessKey="a"
                >
                  <Plus className="h-5 w-5" />
                  Add Row
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowQuickEntryModal(true)}
                  size="lg"
                  className="gap-2 flex-1 min-h-[44px]"
                  title="Quick entry with natural language (Alt+Q)"
                  accessKey="q"
                >
                  <Sparkles className="h-5 w-5" />
                  Quick Entry
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="lg"
                      className="min-h-[44px] px-3"
                      title="More actions"
                      aria-label="More actions"
                    >
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={copyFromLastWeek}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy from Last Week
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setShowAutoFillDialog(true)}
                      disabled={autoFillSuggestions.length === 0}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Smart Autofill
                      {autoFillSuggestions.length > 0 && (
                        <span className="ml-auto flex h-2 w-2" aria-hidden="true">
                          <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-teal-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                        </span>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Anomaly Warnings */}
      {aiWarnings.length > 0 && (
        <AnomalyAlertsPanelEnhanced
          warnings={aiWarnings}
          onDismiss={handleDismissWarning}
        />
      )}

      {/* Smart Auto-Fill Dialog */}
      <SmartAutoFillDialog
        open={showAutoFillDialog}
        onOpenChange={setShowAutoFillDialog}
        suggestions={autoFillSuggestions}
        projects={projects}
        onApply={handleApplyAutoFill}
      />

      {/* Grid */}
      <Card data-tour="timesheet-grid">
        <CardContent className="p-0">
          {/* Status Legend */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 px-4 pt-3 pb-2 text-xs text-muted-foreground border-b bg-muted/30">
            {/* Billable Status Indicators */}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-sm" aria-hidden="true" />
              <span>Billable</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-300 rounded-sm" aria-hidden="true" />
              <span>Non-billable</span>
            </div>
            
            {/* Divider */}
            <div className="hidden sm:block h-4 w-px bg-border" aria-hidden="true" />
            
            {/* Scheduled Booking Status Indicators */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-3 bg-blue-50 border border-blue-200 rounded-sm" aria-hidden="true" />
              <span>Scheduled</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-3 bg-orange-50 border border-orange-200 rounded-sm" aria-hidden="true" />
              <span>Edited</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-3 bg-gray-50 border border-gray-200 rounded-sm" aria-hidden="true" />
              <span>Dismissed</span>
            </div>
            
            {/* Divider */}
            <div className="hidden sm:block h-4 w-px bg-border" aria-hidden="true" />
            
            {/* Absence Entry Indicator */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-3 bg-purple-50 border-2 border-purple-300 rounded-sm" aria-hidden="true" />
              <span>Absence (hover <Lock className="inline h-3 w-3" /> for details)</span>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[180px] sm:w-[250px]">Project</th>
                  <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[120px] sm:w-[140px]">Cost Centre</th>
                  <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[150px] sm:w-[180px]">Description</th>
                  {DAY_LABELS.map((day, i) => (
                    <th key={day} className={`p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm w-[60px] sm:w-[70px] ${i >= 5 ? 'bg-muted' : ''}`}>
                      {day}
                      <div className="text-[10px] sm:text-xs font-normal text-muted-foreground">
                        {format(addDays(currentWeekStart, i), 'dd/MM')}
                      </div>
                    </th>
                  ))}
                  <th className="p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm w-[60px] sm:w-[70px]">Total</th>
                  <th className="p-1.5 sm:p-2 w-[70px] sm:w-[90px]"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => {
                  const project = projects.find(p => p.id === entry.projectId);
                  // Merge project cost centres with enriched global cost centres
                  const enrichedCostCentres = (project?.costCentres || []).map(cc => {
                    const enriched = costCentres.find(ecc => ecc.id === cc.id);
                    return enriched || cc;
                  });
                  
                  // Check if this is a scheduled entry
                  const isScheduled = entry.isFromSchedule || false;
                  const isDismissed = entry.isDismissed || false;
                  const isEdited = isScheduled && !isDismissed && hasEditedHours(entry);
                  
                  // Check if this is an absence entry from HR system
                  const isAbsence = entry.sourceIntegration === 'people-hr';
                  
                  // Debug logging - log ALL entries to see what we have
                  console.log('[TimesheetGrid] Entry:', {
                    id: entry.id,
                    description: entry.description,
                    sourceIntegration: entry.sourceIntegration,
                    isAbsence,
                    isReadOnly: entry.isReadOnly,
                    isNonProjectTime: entry.isNonProjectTime
                  });
                  const isAbsenceReadOnly = isAbsence && entry.isReadOnly;
                  
                  // Extract absence type from entry.absenceType if available, otherwise try to parse from description
                  let absenceType: AbsenceType | null = null;
                  if (isAbsence) {
                    if (entry.absenceType) {
                      absenceType = entry.absenceType;
                    } else if (entry.description) {
                      // Fallback: try to parse from description
                      const parsed = entry.description.toLowerCase().replace(/\s+/g, '-');
                      console.log('[TimesheetGrid] Parsed absence type from description:', { description: entry.description, parsed });
                      
                      // Validate against known absence types
                      const validTypes: AbsenceType[] = [
                        'annual-leave',
                        'sick-leave',
                        'parental-leave',
                        'compassionate-leave',
                        'public-holiday',
                        'unpaid-leave',
                        'study-leave'
                      ];
                      
                      // Map 'training' to 'study-leave' for backwards compatibility
                      if (parsed === 'training' || parsed === 'professional-development-training') {
                        absenceType = 'study-leave';
                      } else if (validTypes.includes(parsed as AbsenceType)) {
                        absenceType = parsed as AbsenceType;
                      }
                    }
                  }
                  
                  // Determine status dot type
                  const statusType: StatusDotType = isDismissed 
                    ? 'dismissed' 
                    : isEdited 
                    ? 'edited' 
                    : 'scheduled';
                  
                  return (
                    <tr 
                      key={entry.id}
                      className={cn(
                        "border-b transition-colors",
                        // Absence entry styling (takes priority)
                        isAbsence && "bg-purple-50/50 hover:bg-purple-100/60 border-l-4 border-l-purple-500",
                        // Row background based on scheduled booking status
                        !isAbsence && isScheduled && !isDismissed && !isEdited && "bg-blue-50/50 hover:bg-blue-100/60",
                        !isAbsence && isScheduled && isEdited && "bg-orange-50/50 hover:bg-orange-100/60",
                        !isAbsence && isScheduled && isDismissed && "bg-gray-50/50 hover:bg-gray-100/60",
                        !isAbsence && !isScheduled && "hover:bg-muted/50",
                        // Billable indicator border (only for non-absence entries)
                        !isAbsence && project?.billable && "border-l-4 border-l-green-500",
                        !isAbsence && !project?.billable && "border-l-4 border-l-gray-300"
                      )}
                      aria-label={
                        isAbsence 
                          ? `Absence entry: ${entry.description}, read-only`
                          : project 
                          ? `${project.name}, ${project.billable ? 'billable' : 'non-billable'} project${isScheduled ? `, scheduled booking (${statusType})` : ', manual entry'}` 
                          : 'No project selected'
                      }
                    >
                      <td className="p-1 sm:p-1.5">
                        {/* Show absence indicator for absence entries */}
                        {isAbsence ? (
                          <div className="flex items-center gap-2">
                            <AbsenceIndicator />
                            <div className="flex-1">
                              <ProjectSelector
                                projects={projects}
                                value={entry.projectId}
                                onChange={() => {}} // No-op for absence entries
                                disabled={true}
                              />
                            </div>
                          </div>
                        ) : isScheduled ? (
                          <BookingHoverCard
                            booking={entry}
                            statusType={statusType}
                          >
                            <div className="w-full">
                              <ProjectSelector
                                projects={projects}
                                value={entry.projectId}
                                onChange={(projectId) => {
                                  updateEntry(index, 'projectId', projectId);
                                  const proj = projects.find(p => p.id === projectId);
                                  if (proj && proj.costCentres.length > 0) {
                                    updateEntry(index, 'costCentreId', proj.costCentres[0].id);
                                  }
                                }}
                                disabled={isReadOnly}
                              />
                            </div>
                          </BookingHoverCard>
                        ) : (
                          <ProjectSelector
                            projects={projects}
                            value={entry.projectId}
                            onChange={(projectId) => {
                              updateEntry(index, 'projectId', projectId);
                              const proj = projects.find(p => p.id === projectId);
                              if (proj && proj.costCentres.length > 0) {
                                updateEntry(index, 'costCentreId', proj.costCentres[0].id);
                              }
                            }}
                            disabled={isReadOnly}
                          />
                        )}
                      </td>
                      <td className="p-1 sm:p-1.5">
                        <CostCentreCombobox
                          costCentres={enrichedCostCentres}
                          value={entry.costCentreId}
                          onChange={(value) => {
                            if (!isAbsence) {
                              updateEntry(index, 'costCentreId', value);
                              updateCostCentreLastUsed(value);
                            }
                          }}
                          onToggleFavourite={toggleCostCentreFavourite}
                          disabled={isAbsence || isReadOnly || !project}
                        />
                      </td>
                      <td className="p-1 sm:p-1.5">
                        {isAbsence ? (
                          <Textarea
                            value={entry.description}
                            onChange={() => {}} // No-op for absence entries
                            placeholder="Activity description..."
                            className="min-h-[50px] sm:min-h-[60px] text-xs sm:text-sm bg-muted/50 cursor-not-allowed"
                            disabled={true}
                          />
                        ) : (
                          <Textarea
                            value={entry.description}
                            onChange={(e) => updateEntry(index, 'description', e.target.value)}
                            placeholder="Activity description..."
                            className="min-h-[50px] sm:min-h-[60px] text-xs sm:text-sm"
                            disabled={isReadOnly}
                          />
                        )}
                      </td>
                      {DAYS.map((day, i) => {
                        const cellKey = getHoursCellKey(entry.id, day);
                        const isEditing = editingHours[cellKey] !== undefined;
                        const displayValue = isEditing
                          ? editingHours[cellKey]!
                          : entry.hours[day] === 0
                            ? ''
                            : entry.hours[day].toFixed(2);
                        
                        return (
                          <td key={day} className={`p-1 ${i >= 5 ? 'bg-muted/30' : ''}`}>
                            <Input
                              type="number"
                              step="0.10"
                              min="0"
                              max="24"
                              value={displayValue}
                              onChange={(e) => {
                                if (!isAbsence) {
                                  setEditingHours(prev => ({ ...prev, [cellKey]: e.target.value }));
                                }
                              }}
                              onBlur={(e) => {
                                if (!isAbsence) {
                                  const raw = e.target.value;
                                  const hours = raw === '' ? 0 : parseFloat(raw);
                                  const rounded = isNaN(hours) ? 0 : Math.round(hours * 10) / 10;
                                  const clamped = Math.max(0, Math.min(24, rounded));
                                  updateHours(index, day, String(clamped));
                                  setEditingHours(prev => {
                                    const next = { ...prev };
                                    delete next[cellKey];
                                    return next;
                                  });
                                }
                              }}
                              placeholder={entry.hours[day] === 0 ? '0' : ''}
                              className={cn(
                                "text-center text-sm sm:text-base font-semibold h-10 sm:h-12 px-1 sm:px-2",
                                entry.hours[day] > 0 ? "text-foreground" : "text-muted-foreground/40",
                                (isReadOnly || isAbsence) && "bg-muted/50 cursor-not-allowed",
                                !isReadOnly && !isAbsence && "hover:border-primary/50 hover:bg-accent/30 transition-colors",
                                isAbsence && entry.hours[day] > 0 && "bg-purple-100/50 border-purple-300"
                              )}
                              disabled={isReadOnly || isAbsence}
                            />
                          </td>
                        );
                      })}
                      <td className={cn(
                        "p-1 sm:p-1.5 text-center font-bold text-sm sm:text-base",
                        rowTotals[index] > 0 ? "text-foreground bg-muted/30" : "text-muted-foreground/50"
                      )}>
                        {rowTotals[index].toFixed(2)}
                      </td>
                      <td className="p-1 sm:p-1.5">
                        <div className="flex items-center gap-0.5 sm:gap-1">
                          {/* No actions for absence entries - icon indicator only */}
                          {isAbsence ? (
                            <div className="flex items-center justify-center w-full">
                              <AbsenceIndicator />
                            </div>
                          ) : (
                            <>
                              {/* Scheduled Booking Actions Menu */}
                              {isScheduled && !isReadOnly && (
                                <BookingActionMenu
                                  booking={entry}
                                  onViewDetails={() => setSelectedBookingForDetails(entry)}
                                  onDismiss={() => setSelectedBookingForDismiss(entry)}
                                  onRestore={() => handleRestoreBooking(index)}
                                  onDelete={() => deleteRow(index)}
                                />
                              )}
                              
                              {/* Standard Actions for Non-Scheduled Entries */}
                              {!isScheduled && !isReadOnly && (
                                <ManualEntryActionMenu
                                  onDuplicate={() => duplicateRow(index)}
                                  onDelete={() => deleteRow(index)}
                                  disabled={isReadOnly}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-muted/70 font-bold">
                  <td colSpan={3} className="p-2 sm:p-2.5 text-right text-xs sm:text-sm">Daily Totals:</td>
                  {columnTotals.map((total, i) => (
                    <td key={i} className={cn(
                      "p-2 sm:p-2.5 text-center text-xs sm:text-sm",
                      i >= 5 ? 'bg-muted' : '',
                      total > 0 && "text-foreground"
                    )}>
                      {total.toFixed(2)}
                    </td>
                  ))}
                  <td className="p-2 sm:p-2.5 text-center text-sm sm:text-base bg-primary/10 text-primary">
                    {weekTotal.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Zone 3: Review & Submission Footer (Sticky) */}
      {!isReadOnly && (
        <div className="sticky bottom-0 z-10 bg-background border-t-2 border-primary/20 shadow-lg">
          <Card className="border-0 rounded-none">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                {/* Week Total Display */}
                <div className="flex items-center gap-3">
                  <div className="text-sm text-muted-foreground">
                    Week Total:
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-primary">
                    {weekTotal.toFixed(2)} hrs
                  </div>
                  {weekTotal > 0 && weekTotal < 35 && (
                    <Badge variant="outline" className="text-xs">
                      Under 35 hrs
                    </Badge>
                  )}
                  {weekTotal > 40 && (
                    <Badge variant="destructive" className="text-xs">
                      Over 40 hrs
                    </Badge>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    size="default"
                    onClick={saveTimesheet}
                    className="flex-1 sm:flex-none gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Save Draft
                  </Button>
                  {isRejected ? (
                    <Button 
                      size="default"
                      onClick={handleResubmit}
                      disabled={entries.length === 0 || hasMissingDescriptions}
                      className="flex-1 sm:flex-none gap-2 font-semibold bg-orange-600 hover:bg-orange-700"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Resubmit for Approval
                    </Button>
                  ) : (
                    <Button 
                      size="default"
                      onClick={submitTimesheet}
                      disabled={entries.length === 0 || hasMissingDescriptions}
                      className="flex-1 sm:flex-none gap-2 font-semibold"
                      data-tour="submit-timesheet"
                    >
                      <Send className="h-4 w-4" />
                      Submit for Approval
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Validation Messages */}
              {entries.length === 0 && (
                <div className="mt-2 text-xs text-muted-foreground text-center">
                  Add at least one timesheet entry to submit
                </div>
              )}
              {entries.length > 0 && hasMissingDescriptions && (
                <div className="mt-2 text-xs text-destructive text-center">
                  Please add descriptions to all entries before submitting
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mobile FAB (Floating Action Button) - Only visible on mobile */}
      {!isReadOnly && (
        <Button
          onClick={addRow}
          size="lg"
          className="fixed bottom-20 right-4 z-30 h-14 w-14 rounded-full shadow-2xl md:hidden hover:scale-110 transition-transform"
          title="Add Row"
        >
          <Plus className="h-6 w-6" />
        </Button>
      )}

      {/* Audit History Dialog */}
      <AuditHistoryDialog
        open={showAuditHistory}
        onOpenChange={setShowAuditHistory}
        timesheetId={currentTimesheet.id}
        employeeName={currentEmployee.name}
        weekEnding={currentTimesheet.weekEnding}
      />

      {/* Quick Entry Modal */}
      <QuickEntryModalEnhanced
        open={showQuickEntryModal}
        onOpenChange={setShowQuickEntryModal}
        projects={projects}
        timesheets={timesheets}
        currentEmployeeId={currentEmployee.id}
        onEntryAdded={handleNaturalLanguageEntry}
      />

      {/* Booking Details Modal */}
      {selectedBookingForDetails && (
        <BookingDetailsModal
          open={!!selectedBookingForDetails}
          onOpenChange={(open) => !open && setSelectedBookingForDetails(null)}
          booking={{
            projectName: projects.find(p => p.id === selectedBookingForDetails.projectId)?.name || 'Unknown Project',
            costCentreName: costCentres.find(cc => cc.id === selectedBookingForDetails.costCentreId)?.name || 'Unknown Cost Centre',
            scheduledHours: selectedBookingForDetails.scheduledHours || {
              monday: 0,
              tuesday: 0,
              wednesday: 0,
              thursday: 0,
              friday: 0,
              saturday: 0,
              sunday: 0,
            },
            bookingCreatedByName: selectedBookingForDetails.bookingCreatedByName,
            bookingCreatedAt: selectedBookingForDetails.bookingCreatedAt,
            bookingNotes: selectedBookingForDetails.bookingNotes,
          }}
          onDismiss={() => {
            const index = entries.findIndex(e => e.id === selectedBookingForDetails.id);
            if (index !== -1) {
              setSelectedBookingForDismiss(selectedBookingForDetails);
              setSelectedBookingForDetails(null);
            }
          }}
        />
      )}

      {/* Dismiss Booking Modal */}
      {selectedBookingForDismiss && (
        <DismissBookingModal
          open={!!selectedBookingForDismiss}
          onOpenChange={(open) => !open && setSelectedBookingForDismiss(null)}
          booking={{
            projectName: projects.find(p => p.id === selectedBookingForDismiss.projectId)?.name || 'Unknown Project',
            scheduledHours: selectedBookingForDismiss.scheduledHours || {
              monday: 0,
              tuesday: 0,
              wednesday: 0,
              thursday: 0,
              friday: 0,
              saturday: 0,
              sunday: 0,
            },
          }}
          onConfirm={(reason, customReason) => {
            const index = entries.findIndex(e => e.id === selectedBookingForDismiss.id);
            if (index !== -1) {
              handleDismissBooking(index, reason, customReason);
            }
            setSelectedBookingForDismiss(null);
          }}
        />
      )}
    </div>
  );
}
